/**
 * AdminRouteGuard - Route guard for role-based access control.
 *
 * This component checks if the user has the required role(s) to access a route.
 * If the user doesn't have the required role, they are redirected to the dashboard.
 *
 * This guard must be used after ProtectedRoute (authentication check).
 */

import type { ReactNode } from 'react'
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthUser, useAuthStatus } from '@/contexts/AuthContext'
import { useUserRole } from '@/hooks/useUserRole'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getIntendedRoute } from './utils/routePreservation'

interface AdminRouteGuardProps {
    children: ReactNode
    /** Array of role IDs that can access this route (e.g., ['support', 'admin', 'platform_owner']) */
    requiredRoles: string[]
}

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({ children, requiredRoles }) => {
    const user = useAuthUser()
    const { role, loading } = useUserRole()
    const { status, hydrated } = useAuthStatus()
    const { t } = useTranslation()
    const location = useLocation()

    // Don't render anything until role is loaded
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: (theme) => theme.spacing(25),
                    p: 3,
                }}
                role="status"
                aria-live="polite"
                aria-label={t('common:loading')}
            >
                <CircularProgress size={24} sx={{ mb: 2 }} />
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {t('common:loading')}
                </Typography>
            </Box>
        )
    }

    // If user is not authenticated, ProtectedRoute should have handled this
    // But as a safety check, redirect if no user
    if (!user) {
        return <Navigate to="/login" replace />
    }

    // If role is not loaded or user doesn't have required role, redirect to dashboard
    // BUT: respect route preservation during auth rehydration
    // If we're still rehydrating or on a preserved route, wait before redirecting
    if (!role || !requiredRoles.includes(role)) {
        const intendedRoute = getIntendedRoute()
        // Extract React Router location properties (not browser location)
        const { pathname, search, hash } = location
        const currentPath = `${pathname}${search}${hash}`
        const isPreservedRoute = intendedRoute && intendedRoute === currentPath
        const isRehydrating = !hydrated || status === 'restoring'

        // During rehydration or if we're on a preserved route, show loading instead of redirecting
        // This allows route preservation to complete before we make redirect decisions
        if (isRehydrating || isPreservedRoute) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: (theme) => theme.spacing(25),
                        p: 3,
                    }}
                    role="status"
                    aria-live="polite"
                    aria-label={t('common:loading')}
                >
                    <CircularProgress size={24} sx={{ mb: 2 }} />
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {t('common:loading')}
                    </Typography>
                </Box>
            )
        }

        // After rehydration is complete and we're not on a preserved route, redirect
        return <Navigate to="/dashboard" replace />
    }

    // User has required role, render the protected content
    return <>{children}</>
}

export default AdminRouteGuard
